import { ExternalLink, GitBranch, Package } from "lucide-react";
import type { AppDetailResponse } from "@/api/client";
import { Badge, Card, CardContent, CardHeader, CardTitle } from "@fox1ck-org/ui";

interface SourceCardProps {
  app: AppDetailResponse;
}

function repoLinkUrl(repoUrl: string, path: string, revision?: string): string {
  // Best-effort GitHub deep link. Falls back to the raw repo URL.
  try {
    const cleaned = repoUrl.replace(/\.git$/, "");
    if (cleaned.includes("github.com")) {
      const ref = revision && revision !== "HEAD" ? revision : "HEAD";
      return `${cleaned}/tree/${ref}/${path}`;
    }
    return cleaned;
  } catch {
    return repoUrl;
  }
}

/**
 * Source-of-truth card. Surfaces the git source, sync policy, and managed
 * container images so operators can validate "what's deployed?" without
 * leaving the dashboard.
 */
export function SourceCard({ app }: SourceCardProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <GitBranch className="h-4 w-4" />
          Источник
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-xs">
        {app.source && (
          <div className="space-y-1">
            <div className="text-muted-foreground">Репозиторий</div>
            <a
              href={repoLinkUrl(app.source.repoURL, app.source.path, app.source.targetRevision)}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-foreground hover:text-primary inline-flex items-center gap-1 break-all"
            >
              {app.source.repoURL.replace(/^https?:\/\/(www\.)?/, "").replace(/\.git$/, "")}
              <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
            <div className="font-mono text-muted-foreground">
              {app.source.path} @ {app.source.targetRevision || "HEAD"}
            </div>
          </div>
        )}

        {app.syncPolicy && (
          <div className="space-y-1">
            <div className="text-muted-foreground">Политика синхронизации</div>
            <div className="flex flex-wrap gap-1">
              {app.syncPolicy.automated ? (
                <>
                  <Badge variant="info" className="text-[10px]">automated</Badge>
                  {app.syncPolicy.automated.prune && (
                    <Badge variant="warn" className="text-[10px]">prune</Badge>
                  )}
                  {app.syncPolicy.automated.selfHeal && (
                    <Badge variant="ok" className="text-[10px]">self-heal</Badge>
                  )}
                </>
              ) : (
                <Badge variant="neutral" className="text-[10px]">manual</Badge>
              )}
            </div>
          </div>
        )}

        {app.images.length > 0 && (
          <div className="space-y-1">
            <div className="text-muted-foreground flex items-center gap-1">
              <Package className="h-3 w-3" />
              Образы
            </div>
            <ul className="space-y-1">
              {app.images.map((image) => (
                <li
                  key={image}
                  className="font-mono text-foreground/80 bg-muted/40 rounded px-2 py-1 break-all"
                >
                  {image}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
